import { useState } from 'react';
import { Check, GripVertical, Trash2 } from 'lucide-react';

import type { Status } from '@tasks/contracts';

import { IconButton } from '../../components/IconButton';
import { STATUS_CATEGORY_ORDER, statusCategoryLabels } from '../../i18n/enums';
import type { StatusCategory } from '../../lib/types';
import styles from './WorkflowSettingsPage.module.css';

export type StatusAssigneeOption = {
  readonly id: string;
  readonly displayName: string;
};

export type StatusRowProps = {
  readonly status: Status;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  /** Участники проекта для выбора ответственного по умолчанию. */
  readonly members: readonly StatusAssigneeOption[];
  readonly onRename: (name: string) => void;
  readonly onCategory: (category: StatusCategory) => void;
  readonly onWipLimit: (wipLimit: number | null) => void;
  readonly onDefaultAssignee: (userId: string | null) => void;
  readonly onMove: (direction: -1 | 1) => void;
  readonly onDelete: () => void;
};

/** Пустое значение селекта: «ответственный не задан». */
const NO_ASSIGNEE = '__none__';

/** Строка статуса: имя по blur, категория, WIP-лимит, ответственный, порядок, удаление. */
export function StatusRow({
  status,
  isFirst,
  isLast,
  members,
  onRename,
  onCategory,
  onWipLimit,
  onDefaultAssignee,
  onMove,
  onDelete,
}: StatusRowProps) {
  const [name, setName] = useState(status.name);
  const [wip, setWip] = useState(status.wipLimit === null ? '' : String(status.wipLimit));

  return (
    <tr>
      <td>
        <input
          className={styles['textInput']}
          value={name}
          aria-label={`Название статуса ${status.name}`}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            const trimmed = name.trim();
            if (trimmed.length === 0) {
              setName(status.name);
              return;
            }
            if (trimmed !== status.name) {
              onRename(trimmed);
            }
          }}
        />
        {status.isInitial ? <span className={styles['badge']}>начальный</span> : null}
      </td>
      <td>
        <select
          className={styles['select']}
          value={status.category}
          aria-label={`Категория статуса ${status.name}`}
          onChange={(event) => onCategory(event.target.value as StatusCategory)}
        >
          {STATUS_CATEGORY_ORDER.map((category) => (
            <option key={category} value={category}>
              {statusCategoryLabels[category]}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          className={styles['numberInput']}
          inputMode="numeric"
          value={wip}
          placeholder="∞"
          aria-label={`WIP-лимит статуса ${status.name}`}
          onChange={(event) => setWip(event.target.value.replace(/[^0-9]/g, ''))}
          onBlur={() => {
            const parsed = wip.length === 0 ? null : Number(wip);
            if (parsed !== status.wipLimit) {
              onWipLimit(parsed);
            }
          }}
        />
      </td>
      <td>
        <select
          className={styles['assigneeSelect']}
          value={status.defaultAssigneeId ?? NO_ASSIGNEE}
          aria-label={`Ответственный по умолчанию для статуса ${status.name}`}
          onChange={(event) =>
            onDefaultAssignee(event.target.value === NO_ASSIGNEE ? null : event.target.value)
          }
        >
          <option value={NO_ASSIGNEE}>Не менять</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>
              {member.displayName}
            </option>
          ))}
        </select>
      </td>
      <td>
        <span className={styles['order']}>
          <IconButton
            size="sm"
            label={`Поднять статус ${status.name}`}
            icon={<GripVertical size={14} />}
            disabled={isFirst}
            onClick={() => onMove(-1)}
          />
          <IconButton
            size="sm"
            label={`Опустить статус ${status.name}`}
            icon={<Check size={14} />}
            disabled={isLast}
            onClick={() => onMove(1)}
          />
        </span>
      </td>
      <td className={styles['cellActions']}>
        <IconButton
          size="sm"
          tone="danger"
          label={`Удалить статус ${status.name}`}
          icon={<Trash2 size={14} />}
          disabled={status.isInitial}
          onClick={onDelete}
        />
      </td>
    </tr>
  );
}
